import { Accordion, AccordionDetails, AccordionSummary, IconButton, List, ListItem, ListItemText, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import theme from '~/theme.js';
import Icon from 'Components/Icon';
import { toggleSites } from 'ducks/ui';
import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';
import { organizationSites } from 'selectors/sites';
import { PANEL_HEADER, SPACER_MINI } from 'utils/ui/constants';
import icons from 'utils/ui/icons';

const SitesCollapsed = styled('div')({
    width: '100%',
    height: PANEL_HEADER + SPACER_MINI
});

const SitesExpanded = styled('div')({
    width: '100%'
});

const StyledAccordion = styled(Accordion)({
    '&:first-of-type': { borderRadius: 0 },
    '&:last-child': { borderRadius: 0 }
});

const StyledAccordionDetails = styled(AccordionDetails)({
    height: 'calc(100vh - 96px)',
    cursor: 'pointer',
    overflow: 'auto',
    padding: 0,
    width: '100%'
});

const StyledAccordionSummary = styled(AccordionSummary)({
    height: PANEL_HEADER,
    minHeight: PANEL_HEADER,
    padding: '0 12px 0 16px'
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    padding: theme.spacing(1),
    fontSize: theme.spacing(2),
    color: alpha(theme.palette.primary.dark, 0.8)
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 'bold'
}));

const StyledLink = styled(Link)({
    cursor: 'pointer',
    padding: '4px 12px',
    textDecoration: 'none',
    width: '100%'
});

const StyledListItem = styled(ListItem)({
    borderBottom: '1px solid #DDDDDD',
    padding: 0,
    ':hover': {
        backgroundColor: theme.palette.primary.light
    },
    '&:nth-of-type(2n + 1)': {
        background: theme.palette.backgrounds.color.light,
        ':hover': {
            backgroundColor: theme.palette.primary.light
        }
    },
    '&:first-of-type': {
        borderTop: '1px solid #DDDDDD'
    }
});

const StyledListText = styled(ListItemText)({
    '& .MuiListItemText-primary': {
        fontSize: '0.65rem'
    }
});

const Component = ({ siteId }) => {
    const site = useSelector(state => state.sites.models[siteId]);
    const sites = useSelector(state => organizationSites(state, { siteId }));
    const panelOpen = useSelector(state => state.ui.showSites);

    const dispatch = useDispatch();
    const handleToggle = () => dispatch(toggleSites());

    const Wrapper = panelOpen ? SitesExpanded : SitesCollapsed;

    return (
        <Wrapper>
            <StyledAccordion disableGutters expanded={panelOpen} onChange={handleToggle}>
                <StyledAccordionSummary component="div" expandIcon={
                    <StyledIconButton>
                        <Icon icon={icons.expand} size="xs"/>
                    </StyledIconButton>
                }>
                    <StyledTypography>{site.name}</StyledTypography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                    <List component="nav" dense>
                        {sites.map(site => (
                            <StyledListItem key={site.id}>
                                <StyledLink onClick={handleToggle} to={`/sites/${site.id}`}>
                                    <StyledListText primary={site.name}/>
                                </StyledLink>
                            </StyledListItem>
                        ))}
                    </List>
                </StyledAccordionDetails>
            </StyledAccordion>
        </Wrapper>
    );
};

Component.propTypes = {
    siteId: PropTypes.string.isRequired
};

export default Component;
